import { AccountFilterOption } from '@/types/analytics';
import { Organization } from '@/types/organization';

export const accountOverviewColumnNameMapping = {
  accountName: 'Account Name',
  patientName: 'Patient Name',
  dateOfBirth: 'Date of Birth',
  ehrMrn: 'EHR-MRN',
  diagnosis: 'Diagnosis',
  vendor: 'Vendor',
  deviceType: 'Device Type',
  modelName: 'Model Name',
  serialNumber: 'Serial Number',
  patientStatus: 'Patient Status',
  patientStatusReason: 'Status Reason',
  followingPractitioner: 'Following Practitioner',
  referringProvider: 'Referring Provider'
} as const;

export const alertColumnNameMapping = {
  accountName: 'Account Name',
  alertType: 'Alert Type',
  billed: 'Billed',
  dateOfService: 'Date of service',
  signedDate: 'Signed Date',
  patientName: 'Patient Name',
  dateOfBirth: 'Date of birth',
  ehrMrn: 'EHR=MRN',
  diagnosis: 'Diagnosis',
  vendor: 'Vendor',
  deviceType: 'Device type',
  modelName: 'Model Name',
  serialNumber: 'Serial Number',
  patientStatus: 'Patient Status',
  patientStatusReason: 'Status Reason',
  followingPractitioner: 'Following Practitioner',
  referringProvider: 'Referring Provider'
} as const;

export const billingColumnNameMapping = {
  accountName: 'Account Name',
  patientName: 'Patient Name',
  dateOfBirth: 'Date of birth',
  ehrMrn: 'EHR=MRN',
  diagnosis: 'Diagnosis',
  dateOfService: 'Date of service',
  signedDate: 'Signed Date',
  vendor: 'Vendor',
  deviceType: 'Device type',
  modelName: 'Model Name',
  serialNumber: 'Serial Number',
  reportType: 'Report Type',
  nextScheduledDate: 'Next Schedule Date',
  followingPractitioner: 'Following Practitioner',
  referringProvider: 'Referring Provider',
  professionalCptCode: 'Professional CPT code',
  technicalCptCode: 'Technical CPT code',
  signingPractitioner: 'Signing Practitioner'
} as const;

export const signatureStatusColumnMapping = {
  practitioner: 'Practitioner',
  redSigned: 'Red Signed',
  redAwaitingSignature: 'Red Awaiting Signature',
  yellowSigned: 'Yellow Signed',
  yellowAwaitingSignature: 'Yellow Awaiting Signature',
  greenSigned: 'Green Signed',
  greenAwaitingSignature: 'Green Awaiting Signature'
} as const;

export const batteryLifeColumnMapping = {
  accountName: 'Account Name',
  patientName: 'Patient Name',
  vendor: 'Vendor',
  deviceType: 'Device Type',
  modelName: 'Model Name',
  batteryPercentRemaining: 'Battery % Remaining',
  batteryLongevity: 'Battery Longevity'
} as const;

export const deviceRecallsColumnMapping = {
  patientName: 'Patient Name',
  vendor: 'Vendor',
  deviceType: 'Device Type',
  modelName: 'Model Name',
  recallClass: 'Recall Class',
  recallStatus: 'Recall Status',
  recallDate: 'Recall Date'
} as const;

export const leadsRecallsColumnMapping = {
  patientName: 'Patient Name',
  vendor: 'Vendor',
  deviceType: 'Device Type',
  modelName: 'Model Name',
  recallClass: 'Recall Class',
  recallStatus: 'Recall Status',
  recallDate: 'Recall Date',
  leadType: 'Lead Type'
} as const;

// Columns with Submenu Options
export const columnsWithSubMenu = [
  'deviceType',
  'vendor',
  'alertType',
  'patientStatus',
  'recallClass',
  'recallStatus',
  'patientStatusReason',
  'reportType',
  'practitioner',
  'leadType',
  'followingPractitioner'
];

export enum DeviceVendors {
  Abbott = 'abbott',
  Biotronik = 'biotronik',
  BostonScientific = 'boston_scientific',
  Medtronic = 'medtronic',
  Sorin = 'sorin'
}

export const VendorsToCodeMapping: Record<string, string> = {
  Abbott: 'ABT',
  'Boston Scientific': 'BSC',
  Medtronic: 'MDT',
  Biotronik: 'BIO',
  Sorin: 'SOR'
};

export const devicesVendorOptions = [
  {
    label: 'Abbott',
    url: 'https://www.merlin.net',
    value: DeviceVendors.Abbott
  },
  {
    label: 'BIOTRONIK',
    url: 'https://www.biotronik-homemonitoring.com/hmsc_guiWeb/user/site/SignIn.jsf',
    value: DeviceVendors.Biotronik
  },
  {
    label: 'Boston Scientific',
    url: 'https://www.latitude.bostonscientific.com/',
    value: DeviceVendors.BostonScientific
  },
  {
    label: 'Medtronic',
    url: 'https://clc.medtroniccarelink.net/Clinician/Login/login.aspx?ReturnUrl=%2fClinician%2fhome.aspx',
    value: DeviceVendors.Medtronic
  },
  {
    label: 'Sorin',
    url: '',
    value: DeviceVendors.Sorin
  }
];

export enum DeviceTypes {
  Pm = 'PM',
  Icd = 'ICD',
  CrtD = 'CRT-D',
  CrtP = 'CRT-P',
  Ilr = 'ILR'
}

export const PatientStatusReasons = [
  'Empty',
  'Account - Account deactivated',
  'Account - Device type not monitored',
  'Billing - Program cost',
  'Compliance - Not responsive to outreach',
  "Compliance - Won't connect to home monitor",
  "Compliance - Won't transmit",
  'Device - End of service',
  'Device - Incompatible with remote monitoring',
  'Device - Unable to be remotely monitored',
  'Duplicate - Already Patient',
  'Eligibility - Insurance plan not covered',
  'Eligibility - Hospice',
  'Eligibility - Currently monitored elsewhere',
  'Eligibility - Moved location',
  'Eligibility - Moved, location not supported',
  'Eligibility - Incarcarated',
  'Eligibility - Unable to verify insurance',
  'Eligibility - Unable to verify demographics',
  'Medical - Phyician request',
  'Medical - Clinic request',
  'Medical - Deceased',
  'Medical - Explanted',
  'Patient Request - Refused monitoring',
  'Other',
  "Patient Request - Doesn't find value in monitoring",
  'RECEIVED PHYSICIAN ORDER FORM'
];

export enum ReportType {
  'In-Clinic' = 'in-clinic',
  Manual = 'manual',
  'Remote-In-Clinic' = 'remote-in-clinic',
  Remote = 'remote'
}

export enum PatientActiveStatus {
  All = 'All',
  Active = 'ACTIVE',
  Inactive = 'INACTIVE',
  Pending = 'PENDING',
  Paused = 'PAUSED',
  New = 'NEW',
  InProgress = 'IN PROGRESS',
  Closed = 'CLOSED',
  Candidate = 'CANDIDATE'
}

export const devicesTypesOptions = Object.values(DeviceTypes).map((device) => ({
  label: device,
  value: device
}));

export const fetchAccountClinicMap = (clinics: Organization[]) => {
  const map = new Map<string, AccountFilterOption>();

  clinics?.forEach((clinic) => {
    const parentId = clinic?.parent?.id;
    if (!parentId) return;

    const account = map.get(parentId) || {
      name: clinic?.parent?.name,
      id: parentId,
      clinics: []
    };

    account.clinics?.push({
      name: clinic?.name,
      id: clinic?.id
    });

    map.set(parentId, account);
  });
  return map;
};

export enum DeviceStatus {
  Inactive = 0,
  Active = 1,
  'New Implant',
  Pending,
  'Entered In Error'
}

export const DEVICE_STATUS_OPTIONS = [
  { label: 'Inactive', value: DeviceStatus.Inactive },
  { label: 'Active', value: DeviceStatus.Active },
  { label: 'New Implant', value: DeviceStatus['New Implant'] },
  { label: 'Pending', value: DeviceStatus.Pending },
  { label: 'Entered In Error', value: DeviceStatus['Entered In Error'] }
];
